// ABOUTME: Family Insights page — trend-focused analytics with time horizon tabs and child filter
// ABOUTME: Charts 2+3 wired in #1607; Chart 1 in #1606; Chart 4 in #1608; backend APIs in #1605

import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Chip,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Insights as InsightsIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import naluV1Theme from '../theme/theme.nalu-v1';
import SharedAppBar from './SharedAppBar';
import FamilySetupWizard from './FamilySetupWizard';
import BehaviorBreakdownChart, { BehaviorBreakdownChartProps } from './BehaviorBreakdownChart';
import { Family, Child } from '../types/api';
import { familyAPI, insightsAPI } from '../services/api';
import { createMobilePageContainerSx } from '../utils/mobileOptimization';
import PositivityRatioTrendChart from './PositivityRatioTrendChart';
import DayOfWeekPatternChart from './DayOfWeekPatternChart';
import HeartDayRateChart from './HeartDayRateChart';
import InsightsDrillDownPanel from './InsightsDrillDownPanel';
import {
  computeTabDateRange,
  computeHeartDayInsight,
  HeartDayDataPoint,
} from '../utils/insightsUtils';

// Time horizon tab definitions
// Tabs unlock progressively based on family age (days since creation)
// All Time and Week are always visible; others unlock at their minAgeDays threshold
const TIME_HORIZON_TABS = [
  { label: 'Week', minAgeDays: 0 },
  { label: 'Month', minAgeDays: 30 },
  { label: '3 Months', minAgeDays: 90 },
  { label: '6 Months', minAgeDays: 180 },
  { label: 'All Time', minAgeDays: 0 },
] as const;

// Default to Month tab (index 1 in the full 5-tab list)
// For new families the tab will clamp to Week which is always index 0
const MONTH_TAB_LABEL = 'Month';
const BUILDING_THRESHOLD_DAYS = 14;
const HELPER_DISMISSED_KEY = 'insightsChartHelperDismissed';

function getFamilyAgeDays(createdAt?: string): number {
  if (!createdAt) return 0;
  const created = new Date(createdAt);
  const now = new Date();
  return Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
}

const FamilyInsightsPage: React.FC = () => {
  const { user } = useAuth();
  const [families, setFamilies] = useState<Family[]>([]);
  const [selectedFamily, setSelectedFamily] = useState<Family | null>(null);
  const [children, setChildren] = useState<Child[]>([]);
  // familyTimezone passed down to chart components for date boundary calculations
  const [familyTimezone, setFamilyTimezone] = useState<string | undefined>(undefined);
  const [familyCreatedAt, setFamilyCreatedAt] = useState<string | undefined>(undefined);
  const [familyDetailsLoaded, setFamilyDetailsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedChildId, setSelectedChildId] = useState<number | 'all'>('all');

  // Set visited flag so the discovery banner on the old analytics page auto-hides
  useEffect(() => {
    localStorage.setItem('familyInsightsVisited', 'true');
  }, []);

  useEffect(() => {
    if (user) {
      loadFamilyData();
    }
  }, [user]);

  useEffect(() => {
    if (selectedFamily) {
      loadChildren(selectedFamily.familyId);
      loadFamilyTimezone(selectedFamily.familyId);
      loadFamilyDetails(selectedFamily.familyId);
    }
  }, [selectedFamily]);

  const loadFamilyData = async () => {
    try {
      setIsLoading(true);
      const familiesData = await familyAPI.getFamilies();
      setFamilies(familiesData);
      if (familiesData.length > 0) {
        setSelectedFamily(familiesData[0]);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to load family data:', err);
      setError('Unable to load family data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadChildren = async (familyId: number) => {
    try {
      const childrenData = await familyAPI.getChildren(familyId);
      setChildren(childrenData);
    } catch (err) {
      console.error('Failed to load children:', err);
    }
  };

  const loadFamilyTimezone = async (familyId: number) => {
    try {
      const tz = await familyAPI.getFamilyTimezone(familyId);
      setFamilyTimezone(tz);
    } catch (err) {
      console.error('Failed to load family timezone:', err);
    }
  };

  const loadFamilyDetails = async (familyId: number) => {
    try {
      const details = await familyAPI.getFamilyDetails(familyId);
      setFamilyCreatedAt(details.createdAt);
    } catch (err) {
      console.error('Failed to load family details:', err);
    } finally {
      setFamilyDetailsLoaded(true);
    }
  };

  const handleAppBarMenuClick = (action: string) => {
    if (action.startsWith('/')) {
      window.location.href = action;
    } else if (action === 'Dashboard') {
      window.location.href = '/';
    }
  };

  if (!isLoading && families.length === 0) {
    return <FamilySetupWizard />;
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <SharedAppBar title="Family Insights" onMenuClick={handleAppBarMenuClick} />
        <Box sx={createMobilePageContainerSx()}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Box>
    );
  }

  const familyAgeDays = getFamilyAgeDays(familyCreatedAt);
  const isBuildingBaseline = familyAgeDays < BUILDING_THRESHOLD_DAYS;

  // Only show tabs the family has enough history for; Week and All Time are always visible
  const visibleTabs = TIME_HORIZON_TABS.filter(
    tab => tab.minAgeDays === 0 || familyAgeDays >= tab.minAgeDays
  );

  // Default to Month tab if it's visible, otherwise Week (index 0)
  const defaultTabIndex = visibleTabs.findIndex(t => t.label === MONTH_TAB_LABEL);
  const initialTab = defaultTabIndex >= 0 ? defaultTabIndex : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <SharedAppBar title="Family Insights" onMenuClick={handleAppBarMenuClick} />

      <Box sx={createMobilePageContainerSx({ flex: 1 })}>
        {isLoading || !familyDetailsLoaded ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading...</Typography>
          </Box>
        ) : (
          <TabsContent
            visibleTabs={visibleTabs}
            initialTab={initialTab}
            childList={children}
            selectedChildId={selectedChildId}
            setSelectedChildId={setSelectedChildId}
            isBuildingBaseline={isBuildingBaseline}
            familyId={selectedFamily!.familyId}
            familyTimezone={familyTimezone}
            familyCreatedAt={familyCreatedAt}
          />
        )}
      </Box>
    </Box>
  );
};

// Separate component so tab state is stable once data has loaded
interface TabsContentProps {
  visibleTabs: typeof TIME_HORIZON_TABS[number][];
  initialTab: number;
  childList: Child[];
  selectedChildId: number | 'all';
  setSelectedChildId: (id: number | 'all') => void;
  isBuildingBaseline: boolean;
  familyId: number;
  familyTimezone: string | undefined;
  familyCreatedAt: string | undefined;
}

const TabsContent: React.FC<TabsContentProps> = ({
  visibleTabs,
  initialTab,
  childList,
  selectedChildId,
  setSelectedChildId,
  isBuildingBaseline,
  familyId,
  familyTimezone,
  familyCreatedAt,
}) => {
  const theme = useTheme();
  // Compared by value (logoSrc), not object identity — dev-server HMR can give the
  // ThemeProvider's resolved theme and this module's naluV1Theme import different
  // object references after a hot-reload even though both are "the nalu-v1 theme".
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedDrillDownDate, setSelectedDrillDownDate] = useState<string | null>(null);
  const [helperDismissed, setHelperDismissed] = useState(
    () => localStorage.getItem(HELPER_DISMISSED_KEY) === 'true'
  );

  // Per-child heart day trend data: childId → data points
  const [heartRateData, setHeartRateData] = useState<Map<number, HeartDayDataPoint[]>>(new Map());
  // Per-child all-time averages: childId → 0–1
  const [allTimeAverages, setAllTimeAverages] = useState<Record<number, number>>({});
  // Per-child current streak: childId → streak count
  const [streaks, setStreaks] = useState<Record<number, number>>({});
  const [isLoadingChart, setIsLoadingChart] = useState(false);

  const activeTabLabel = visibleTabs[activeTab]?.label ?? 'Month';
  const { startDate, endDate, granularity, isDayGranularity } = computeTabDateRange(
    activeTabLabel,
    familyCreatedAt,
    familyTimezone,
  );

  // Reset drill-down when switching to non-day tabs
  useEffect(() => {
    if (!isDayGranularity) {
      setSelectedDrillDownDate(null);
    }
  }, [isDayGranularity]);

  // Load chart data when tab or child filter changes
  useEffect(() => {
    if (!familyId || childList.length === 0) return;

    const childrenToLoad = selectedChildId === 'all'
      ? childList
      : childList.filter(c => (c.id ?? c.childId) === selectedChildId);

    setIsLoadingChart(true);

    const loadData = async () => {
      try {
        const newHeartRateData = new Map<number, HeartDayDataPoint[]>();
        const newAllTimeAverages: Record<number, number> = {};
        const newStreaks: Record<number, number> = {};

        await Promise.all(childrenToLoad.map(async child => {
          const childId = (child.id ?? child.childId) as number;
          const [data, avg, streak] = await Promise.all([
            insightsAPI.getHeartDayRateOverTime(familyId, startDate, endDate, granularity, childId, familyTimezone),
            insightsAPI.getAllTimeAverageHeartDayRate(familyId, childId, familyTimezone),
            insightsAPI.getChildCurrentHeartStreak(childId),
          ]);
          newHeartRateData.set(childId, data);
          newAllTimeAverages[childId] = avg;
          newStreaks[childId] = streak;
        }));

        setHeartRateData(newHeartRateData);
        setAllTimeAverages(newAllTimeAverages);
        setStreaks(newStreaks);
      } catch (err) {
        console.error('Failed to load chart data:', err);
      } finally {
        setIsLoadingChart(false);
      }
    };

    loadData();
    // childList intentionally excluded — use .length as proxy to avoid infinite re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedChildId, familyId, familyTimezone, childList.length, startDate, endDate, granularity]);

  const handlePointClick = (date: string) => {
    setSelectedDrillDownDate(date);
    if (!helperDismissed) {
      setHelperDismissed(true);
      localStorage.setItem(HELPER_DISMISSED_KEY, 'true');
    }
  };

  // Build children data for chart
  const childrenToDisplay = selectedChildId === 'all'
    ? childList
    : childList.filter(c => (c.id ?? c.childId) === selectedChildId);

  const chartChildrenData = childrenToDisplay.map(child => {
    const childId = (child.id ?? child.childId) as number;
    return { child, data: heartRateData.get(childId) ?? [] };
  });

  // Compute headline insights from trend data + streaks
  const insightLines = childrenToDisplay.map(child => {
    const childId = (child.id ?? child.childId) as number;
    const data = heartRateData.get(childId) ?? [];
    const streak = streaks[childId] ?? 0;
    return computeHeartDayInsight(child.name, data, streak, activeTabLabel);
  });

  // Build heart day status for drill-down: childId → bool (successRate = 1 means heart day)
  const heartDayStatusByChild: Record<number, boolean> = {};
  if (selectedDrillDownDate) {
    childrenToDisplay.forEach(child => {
      const childId = (child.id ?? child.childId) as number;
      const data = heartRateData.get(childId) ?? [];
      const point = data.find(d => d.period === selectedDrillDownDate);
      heartDayStatusByChild[childId] = (point?.successRate ?? 0) === 1;
    });
  }

  const hasRealInsights = insightLines.some(l => l.emoji !== '');

  return (
    <>
      {/* Time Horizon Tabs */}
      {/* nalu-v1's pill tabs (issue #1900) carry selection via fill, not an underline —
          the divider beneath them is dropped to match the mockup. */}
      <Box sx={{ borderBottom: isNaluV1Theme ? 0 : 1, borderColor: 'divider', mb: isNaluV1Theme ? 3 : 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Time horizon tabs"
        >
          {visibleTabs.map((tab, index) => (
            <Tab
              key={tab.label}
              label={tab.label}
              id={`insights-tab-${index}`}
              aria-controls={`insights-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Box>

      {/* Child Filter Chips */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          overflowX: 'auto',
          gap: 1,
          mb: 3,
          pb: 0.5,
        }}
        role="group"
        aria-label="Filter by child"
      >
        <Chip
          label="All Family"
          onClick={() => setSelectedChildId('all')}
          // nalu-v1 fills the active family/child filter chip in coral (secondary) rather
          // than teal, distinguishing it from the time horizon tabs above (issue #1900)
          color={selectedChildId === 'all' ? (isNaluV1Theme ? 'secondary' : 'primary') : 'default'}
          variant={selectedChildId === 'all' ? 'filled' : 'outlined'}
          aria-pressed={selectedChildId === 'all'}
          sx={isNaluV1Theme ? { borderRadius: '999px' } : undefined}
        />
        {childList.map(child => {
          const childId = child.id || child.childId;
          return (
            <Chip
              key={childId}
              label={child.name}
              onClick={() => setSelectedChildId(childId as number)}
              color={selectedChildId === childId ? (isNaluV1Theme ? 'secondary' : 'primary') : 'default'}
              variant={selectedChildId === childId ? 'filled' : 'outlined'}
              aria-pressed={selectedChildId === childId}
              sx={isNaluV1Theme ? { borderRadius: '999px' } : undefined}
            />
          );
        })}
      </Box>

      {/* Headline Insight Statement */}
      {/* nalu-v1 wraps each insight line in its own cream pill, matching the mockup's
          per-line callout treatment (issue #1900) — background.paper is the same cream
          fill Card/Paper already use, so this reads as a nested "chip" rather than a new color. */}
      <Box sx={{ mb: 2 }}>
        {!hasRealInsights ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              ...(isNaluV1Theme && { bgcolor: theme.palette.background.paper, borderRadius: '12px', px: 2, py: 1.25 }),
            }}
          >
            <InsightsIcon color="primary" fontSize="small" />
            <Typography variant={isNaluV1Theme ? 'body2' : 'body1'} color="text.secondary" fontStyle="italic">
              Keep logging to unlock insights.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: isNaluV1Theme ? 1 : 0.5 }}>
            {insightLines.map((line, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  ...(isNaluV1Theme && { bgcolor: theme.palette.background.paper, borderRadius: '12px', px: 2, py: 1.25 }),
                }}
              >
                {line.emoji ? (
                  <Typography variant={isNaluV1Theme ? 'body2' : 'body1'}>{line.emoji} {line.message}</Typography>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InsightsIcon color="primary" fontSize="small" />
                    <Typography variant={isNaluV1Theme ? 'body2' : 'body1'} color="text.secondary" fontStyle="italic">
                      {line.message}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Recording Health — building state or placeholder */}
      {isBuildingBaseline ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          You&apos;re building your baseline — trends become meaningful after a few weeks of logging. Keep going!
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 1 }}>
          Recording health data coming soon.
        </Alert>
      )}

      {/* Goals Count Placeholder */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        🎯 Goals completed this period: coming soon
      </Typography>

      {/* Chart Areas */}
      <Box
        id={`insights-tabpanel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`insights-tab-${activeTab}`}
      >
        {/* Chart 1: Heart Day Rate — full width */}
        <Card sx={{ mb: 3 }} data-testid="chart-heart-day-rate">
          <CardContent sx={isNaluV1Theme ? { p: 3 } : undefined}>
            <Typography variant={isNaluV1Theme ? 'h4' : 'h6'} gutterBottom>
              ❤️ Heart Day Rate
            </Typography>
            {isLoadingChart ? (
              <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <HeartDayRateChart
                childrenData={chartChildrenData}
                allTimeAverages={allTimeAverages}
                isDayGranularity={isDayGranularity}
                granularity={granularity}
                startDate={startDate}
                endDate={endDate}
                onPointClick={handlePointClick}
                helperTextDismissed={helperDismissed}
              />
            )}
          </CardContent>
        </Card>

        {/* Charts 2 + 3: Half width on desktop, stacked on mobile */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3, alignItems: 'stretch' }}>
          <Card data-testid="chart-positivity-ratio" sx={{ height: '100%' }}>
            <CardContent sx={isNaluV1Theme ? { p: 3 } : undefined}>
              <PositivityRatioTrendChart
                familyId={familyId}
                childList={childList}
                selectedChildId={selectedChildId}
                activeTabLabel={visibleTabs[activeTab]?.label ?? 'Month'}
                familyTimezone={familyTimezone}
                familyCreatedAt={familyCreatedAt}
              />
            </CardContent>
          </Card>

          <Card data-testid="chart-day-of-week" sx={{ height: '100%' }}>
            <CardContent sx={isNaluV1Theme ? { p: 3 } : undefined}>
              <DayOfWeekPatternChart
                familyId={familyId}
                selectedChildId={selectedChildId}
                activeTabLabel={visibleTabs[activeTab]?.label ?? 'Month'}
                familyTimezone={familyTimezone}
                familyCreatedAt={familyCreatedAt}
              />
            </CardContent>
          </Card>
        </Box>

        {/* Chart 4: Behavior Breakdown — full width */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={isNaluV1Theme ? { p: 3 } : undefined}>
            {/* Title rendered inside BehaviorBreakdownChart to include granularity */}
            {visibleTabs[activeTab] && (
              <BehaviorBreakdownChart
                familyId={familyId}
                childId={selectedChildId}
                tab={visibleTabs[activeTab].label as BehaviorBreakdownChartProps['tab']}
                familyTimezone={familyTimezone}
                familyCreatedAt={familyCreatedAt}
                isBuildingBaseline={isBuildingBaseline}
              />
            )}
          </CardContent>
        </Card>

        {/* Day Drill-Down Panel — shown when a Chart 1 point is clicked */}
        {selectedDrillDownDate && (
          <InsightsDrillDownPanel
            date={selectedDrillDownDate}
            familyId={familyId}
            familyTimezone={familyTimezone}
            selectedChildId={selectedChildId}
            familyChildren={childList}
            heartDayStatusByChild={heartDayStatusByChild}
            onClose={() => setSelectedDrillDownDate(null)}
          />
        )}
      </Box>
    </>
  );
};

export default FamilyInsightsPage;
